/**
 * BridgeHost — MNBP/1 dispatcher (spec §2, §4).
 *
 * Owns the view registry and subscription bookkeeping; routes protocol
 * methods to pluggable ViewBindings so the same host logic serves the real
 * embed page (MIMapBinding / MISceneViewBinding) and headless conformance
 * hosts (MemoryViewBinding in scripts/native-bridge-host-sim.ts).
 */
import {
  MNBP_PROTOCOL_VERSION, HOST_CAPABILITIES,
  ERR_HOST, ERR_METHOD_NOT_FOUND, ERR_INVALID_PARAMS,
  ERR_UNKNOWN_VIEW, ERR_VIEW_KIND, ERR_ENGINE, ERR_UNSUPPORTED, ERR_NOT_READY,
  BridgeMethodError, parseRequest, makeResult, makeError, makeEvent,
  type BridgeRequest,
} from './protocol';
import type { HostTransport } from './transports';

export interface ViewBinding {
  readonly kind: '2d' | '3d';
  /** Execute a domain method (map.* / scene.* / layers.* / markers.* / draw.* / bridge.screenshot). */
  call(method: string, params: Record<string, any>): unknown | Promise<unknown>;
  /** Attach an event emitter for one event type; return an unsubscribe fn, or null if unsupported. */
  subscribe(type: string, emit: (data: unknown) => void, opts?: { throttleMs?: number }): (() => void) | null;
  destroy(): void;
}

export interface ViewFactory {
  createMap(view: string, options: Record<string, any>): ViewBinding | Promise<ViewBinding>;
  createScene(view: string, options: Record<string, any>): ViewBinding | Promise<ViewBinding>;
}

export interface BridgeHostOptions {
  sdkVersion?: string;
  capabilities?: string[];
  onAuthConfigure?: (params: { apiKey?: string; apiBaseUrl?: string }) => void;
  /** Handler for analysis.* ops (op = name after "analysis."). Absent → ERR_UNSUPPORTED. */
  analysis?: (op: string, params: Record<string, any>) => unknown;
}

interface ViewEntry {
  binding: ViewBinding;
  subs: Map<string, () => void>;
}

export class BridgeHost {
  private views = new Map<string, ViewEntry>();
  /** View names reserved by an in-flight create (guards concurrent create races). */
  private pending = new Set<string>();
  private transport: HostTransport | null = null;
  private helloDone = false;

  constructor(private factory: ViewFactory, private opts: BridgeHostOptions = {}) {}

  attach(transport: HostTransport): void | Promise<void> {
    this.transport = transport;
    this.helloDone = false; // a new transport means a new peer: require a fresh handshake
    return transport.start({
      onFrame: (raw) => { void this.onFrame(raw); },
      onClose: () => { /* keep views alive for reattach */ },
    });
  }

  /** Total views (used by embed page layout). */
  get viewCount(): number { return this.views.size; }

  destroy(): void {
    Array.from(this.views.keys()).forEach((view) => this.destroyView(view));
    this.transport?.close();
    this.transport = null;
  }

  // ─── Frame pipeline ────────────────────────────────────────────────────

  async onFrame(raw: string): Promise<void> {
    let req: BridgeRequest;
    try {
      req = parseRequest(raw);
    } catch (e) {
      const be = e as BridgeMethodError;
      this.sendRaw(makeError(null, be.code, be.message));
      return;
    }
    if (req.id === undefined) {
      // Relay signals the native peer dropped: whoever joins the session next
      // must complete a fresh bridge.hello before issuing commands.
      if (req.method === 'bridge.peerDisconnected') this.helloDone = false;
      return; // other notifications from clients are ignored in v1
    }

    try {
      const result = await this.dispatch(req.method, (req.params ?? {}) as Record<string, any>);
      this.sendRaw(makeResult(req.id, result));
    } catch (e) {
      if (e instanceof BridgeMethodError) {
        this.sendRaw(makeError(req.id, e.code, e.message, e.data));
      } else {
        this.sendRaw(makeError(req.id, ERR_ENGINE, (e as Error)?.message ?? 'engine error'));
      }
    }
  }

  private sendRaw(frame: string): void {
    this.transport?.send(frame);
  }

  sendEvent(view: string, type: string, data: unknown): void {
    this.sendRaw(makeEvent(view, type, data));
  }

  // ─── Dispatch ──────────────────────────────────────────────────────────

  private async dispatch(method: string, params: Record<string, any>): Promise<unknown> {
    // Spec §2.2: the first request on any transport MUST be bridge.hello.
    if (!this.helloDone && method !== 'bridge.hello' && method !== 'bridge.ping') {
      throw new BridgeMethodError(ERR_NOT_READY, `bridge.hello handshake required before '${method}'`);
    }
    if (method.startsWith('bridge.')) return this.dispatchBridge(method, params);
    if (method === 'auth.configure') {
      this.opts.onAuthConfigure?.({ apiKey: params.apiKey, apiBaseUrl: params.apiBaseUrl });
      return {};
    }
    if (method.startsWith('analysis.')) {
      if (!this.opts.analysis) throw new BridgeMethodError(ERR_UNSUPPORTED, 'analysis not supported by this host');
      return await this.opts.analysis(method.slice('analysis.'.length), params);
    }

    // View-scoped domain methods.
    const ns = method.split('.', 1)[0];
    if (!['map', 'scene', 'layers', 'markers', 'draw'].includes(ns)) {
      throw new BridgeMethodError(ERR_METHOD_NOT_FOUND, `unknown method '${method}'`);
    }
    const entry = this.requireView(params);
    if (ns === 'map' && entry.binding.kind !== '2d') {
      throw new BridgeMethodError(ERR_VIEW_KIND, `map.* requires a 2d view`);
    }
    if (ns === 'scene' && entry.binding.kind !== '3d') {
      throw new BridgeMethodError(ERR_VIEW_KIND, `scene.* requires a 3d view`);
    }
    return await entry.binding.call(method, params);
  }

  private async dispatchBridge(method: string, params: Record<string, any>): Promise<unknown> {
    switch (method) {
      case 'bridge.hello': {
        if (params.protocol !== MNBP_PROTOCOL_VERSION) {
          throw new BridgeMethodError(ERR_HOST,
            `protocol mismatch: host speaks MNBP/${MNBP_PROTOCOL_VERSION}, client sent ${params.protocol}`);
        }
        this.helloDone = true;
        return {
          protocol: MNBP_PROTOCOL_VERSION,
          host: 'migis-embed',
          sdkVersion: this.opts.sdkVersion ?? '0.0.0',
          capabilities: this.opts.capabilities ?? [...HOST_CAPABILITIES],
        };
      }
      case 'bridge.ping':
        return { t: Date.now() };
      case 'bridge.listViews':
        return {
          views: Array.from(this.views.entries()).map(([view, e]) => ({ view, kind: e.binding.kind, ready: true })),
        };
      case 'bridge.createMap':
      case 'bridge.createScene': {
        const view = typeof params.view === 'string' && params.view ? params.view : 'main';
        if (this.views.has(view) || this.pending.has(view)) {
          throw new BridgeMethodError(ERR_HOST, `view '${view}' already exists`);
        }
        const options = (params.options ?? {}) as Record<string, any>;
        await this.createViewDirect(method === 'bridge.createMap' ? '2d' : '3d', view, options);
        return { view };
      }
      case 'bridge.destroyView': {
        const view = this.requireViewName(params);
        this.destroyView(view);
        return {};
      }
      case 'bridge.subscribe': {
        const entry = this.requireView(params);
        const view = this.requireViewName(params);
        const events = this.requireEvents(params);
        const throttleMs = typeof params.throttleMs === 'number' ? params.throttleMs : undefined;
        for (const type of events) {
          if (entry.subs.has(type)) continue;
          const unsub = entry.binding.subscribe(type, (data) => this.sendEvent(view, type, data), { throttleMs });
          if (unsub) entry.subs.set(type, unsub);
        }
        return { active: Array.from(entry.subs.keys()) };
      }
      case 'bridge.unsubscribe': {
        const entry = this.requireView(params);
        const events = this.requireEvents(params);
        for (const type of events) {
          entry.subs.get(type)?.();
          entry.subs.delete(type);
        }
        return { active: Array.from(entry.subs.keys()) };
      }
      case 'bridge.screenshot': {
        const entry = this.requireView(params);
        return await entry.binding.call('bridge.screenshot', params);
      }
      default:
        throw new BridgeMethodError(ERR_METHOD_NOT_FOUND, `unknown method '${method}'`);
    }
  }

  // ─── Helpers ───────────────────────────────────────────────────────────

  /** Programmatic view creation (embed page `autocreate`, tests). No-op if the view exists. */
  async createViewDirect(kind: '2d' | '3d', view = 'main', options: Record<string, any> = {}): Promise<void> {
    if (this.views.has(view) || this.pending.has(view)) return;
    this.pending.add(view); // reserve synchronously so concurrent creates for the same name can't race
    try {
      const binding = kind === '2d'
        ? await this.factory.createMap(view, options)
        : await this.factory.createScene(view, options);
      this.views.set(view, { binding, subs: new Map() });
      this.sendEvent(view, 'view.created', { view, kind: binding.kind });
    } finally {
      this.pending.delete(view);
    }
  }

  private destroyView(view: string): void {
    const entry = this.views.get(view);
    if (!entry) return;
    entry.subs.forEach((unsub) => { try { unsub(); } catch { /* detached */ } });
    entry.subs.clear();
    try { entry.binding.destroy(); } catch { /* engine already gone */ }
    this.views.delete(view);
    this.sendEvent(view, 'view.destroyed', { view, kind: entry.binding.kind });
  }

  private requireViewName(params: Record<string, any>): string {
    const view = typeof params.view === 'string' && params.view ? params.view : 'main';
    return view;
  }

  private requireView(params: Record<string, any>): ViewEntry {
    const view = this.requireViewName(params);
    const entry = this.views.get(view);
    if (!entry) throw new BridgeMethodError(ERR_UNKNOWN_VIEW, `unknown view '${view}'`);
    return entry;
  }

  private requireEvents(params: Record<string, any>): string[] {
    if (!Array.isArray(params.events) || params.events.some((e: any) => typeof e !== 'string')) {
      throw new BridgeMethodError(ERR_INVALID_PARAMS, 'events must be a string array');
    }
    return params.events as string[];
  }
}
